/**
 * The sample roster's state — the whole of it, and it never leaves the browser.
 *
 * Sample mode has to be genuinely usable (a teacher learning the portal should be able to
 * check a student in, correct it to absent, and check them out) without a single write
 * reaching Supabase. So the sample day lives in `localStorage` under one key, and the
 * attendance hooks divert to these functions whenever they are handed a `sample-` id.
 *
 * The transitions below deliberately mirror the guards on the real mutations — check-in
 * only from `not_arrived`/`absent`, absence only from `not_arrived`/`checked_in`, an undo
 * only from `absent`, and an absence correction wipes the check-in stamps — so what the
 * sample teaches is what the real roster does.
 *
 * The state is scoped to a single calendar day: opening the portal tomorrow reseeds rather
 * than showing yesterday's half-recorded class.
 */

import { getTodaySessionDate } from '../../utils/schedule';
import { REAL_SESSION_KEY, type AttendanceRecord } from '../../types/attendance';
import { SAMPLE_STUDENTS } from './sampleData';

const STORAGE_KEY = 'bhcs.staff-sample-roster.v1';

interface SampleState {
  /** The `YYYY-MM-DD` the seeded day belongs to. */
  day: string;
  records: AttendanceRecord[];
}

/**
 * Mirror of what is in storage, and the only copy when storage is unavailable (private
 * browsing, a test environment, a locked-down device). Sample mode keeps working either
 * way; it just stops surviving a reload.
 */
let memoryState: SampleState | null = null;

function minutesAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

function seedState(day: string): SampleState {
  const now = new Date().toISOString();
  const records: AttendanceRecord[] = SAMPLE_STUDENTS.map(student => {
    const { seed } = student;
    return {
      id: student.recordId,
      studentId: student.studentId,
      programId: student.programId,
      enrollmentId: student.enrollmentId,
      sessionDate: day,
      status: seed.status,
      checkedInAt: seed.checkedInMinutesAgo != null ? minutesAgo(seed.checkedInMinutesAgo) : null,
      checkedInBy: seed.checkedInMinutesAgo != null ? 'teacher' : null,
      checkedInByUserId: null,
      checkedInByName: seed.checkedInByName ?? null,
      pickedUpAt: seed.pickedUpMinutesAgo != null ? minutesAgo(seed.pickedUpMinutesAgo) : null,
      pickedUpByName: seed.pickedUpByName ?? null,
      pickupIsAuthorized: seed.pickupIsAuthorized ?? null,
      pickupRecordedByUserId: null,
      pickupRecordedByName: seed.pickupRecordedByName ?? null,
      checkedOutBy: seed.pickedUpMinutesAgo != null ? 'teacher' : null,
      parentDismissedAt: null,
      absenceReason: seed.absenceReason ?? null,
      // The sample class is after-school; only the parent query populates this for real rows.
      programType: 'after-school',
      // Sample mode demonstrates the after-school roster, which is a real session.
      virtualClassKey: REAL_SESSION_KEY,
      createdAt: now,
      updatedAt: now,
    };
  });
  return { day, records };
}

function persist(state: SampleState): void {
  memoryState = state;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage is optional — `memoryState` already holds the day.
  }
}

function loadState(): SampleState {
  const today = getTodaySessionDate();

  if (memoryState?.day === today) return memoryState;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as SampleState;
      if (parsed?.day === today && Array.isArray(parsed.records)) {
        memoryState = parsed;
        return parsed;
      }
    }
  } catch {
    // Unreadable or corrupt: fall through and seed a fresh day.
  }

  const seeded = seedState(today);
  persist(seeded);
  return seeded;
}

function updateRecord(recordId: string, patch: (record: AttendanceRecord) => AttendanceRecord | null): void {
  const state = loadState();
  const index = state.records.findIndex(r => r.id === recordId);
  if (index === -1) return;

  const next = patch(state.records[index]);
  // A null patch is a rejected transition — the same no-op the real `.in('status', …)`
  // filters produce when a row has moved on.
  if (!next) return;

  const records = state.records.slice();
  records[index] = { ...next, updatedAt: new Date().toISOString() };
  persist({ ...state, records });
}

// ── Reads ─────────────────────────────────────────────────────────────────────

/** The sample attendance rows for one program on one day. */
export function getSampleRecords(programId: string, sessionDate: string): AttendanceRecord[] {
  const state = loadState();
  if (sessionDate !== state.day) return [];
  return state.records.filter(r => r.programId === programId);
}

// ── Writes (browser only) ─────────────────────────────────────────────────────

export function sampleCheckIn(recordId: string, teacherName?: string | null): void {
  updateRecord(recordId, record => {
    if (record.status !== 'not_arrived' && record.status !== 'absent') return null;
    return {
      ...record,
      status: 'checked_in',
      checkedInAt: new Date().toISOString(),
      checkedInBy: 'teacher',
      checkedInByName: teacherName ?? null,
      absenceReason: null,
    };
  });
}

export function sampleMarkAbsent(recordId: string, reason?: string | null): void {
  updateRecord(recordId, record => {
    if (record.status !== 'not_arrived' && record.status !== 'checked_in') return null;
    return {
      ...record,
      status: 'absent',
      absenceReason: reason ?? null,
      checkedInAt: null,
      checkedInBy: null,
      checkedInByUserId: null,
      checkedInByName: null,
    };
  });
}

export function sampleClearAbsence(recordId: string): void {
  updateRecord(recordId, record => {
    if (record.status !== 'absent') return null;
    return {
      ...record,
      status: 'not_arrived',
      absenceReason: null,
      checkedInAt: null,
      checkedInBy: null,
      checkedInByUserId: null,
      checkedInByName: null,
    };
  });
}

export function sampleMarkPickedUp(
  recordId: string,
  { pickedUpByName, isAuthorized, recordedByName }: {
    /** Null when an enrichment class simply ended and nobody collected the child. */
    pickedUpByName: string | null;
    isAuthorized: boolean | null;
    recordedByName?: string | null;
  },
): void {
  updateRecord(recordId, record => ({
    ...record,
    status: 'picked_up',
    pickedUpAt: new Date().toISOString(),
    pickedUpByName,
    pickupIsAuthorized: isAuthorized,
    pickupRecordedByName: recordedByName ?? null,
    checkedOutBy: 'teacher',
  }));
}

/** Throw the sample day away and start it over from the seed. */
export function resetSampleData(): void {
  memoryState = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
  loadState();
}

/** The authorized-pickup options the check-out sheet offers for a sample student. */
export function sampleGuardians(studentId: string) {
  const student = SAMPLE_STUDENTS.find(s => s.studentId === studentId);
  return {
    guardians: student?.guardians ?? [],
    authorizedPickups: student?.authorizedPickups ?? [],
  };
}
